/**
 * P1-01 · Product & variant validation shapes.
 *
 * The single source of truth for product/option/variant input shapes, imported
 * by the admin CRUD router (P1-07) rather than redefined there. Everything here
 * is `template:core` and brand-agnostic — option *names* and *values* (Colour,
 * Size, Red, …) are user data, never enums, so no apparel string literals live
 * in source (P1-01 acceptance).
 *
 * Money rule (P0-05): amounts are integer minor units — `.int()` rejects any
 * Float/Decimal at the boundary; currency is a 3-char ISO-4217 code.
 */
import { z } from "zod";
import { CATALOG_READINESS_VALUES } from "~/lib/admin/catalogue-quality";

import { videoUrlSchema } from "~/server/api/schemas/product-media";

export const productStatusSchema = z.enum(["DRAFT", "ACTIVE", "ARCHIVED"]);
export type ProductStatusInput = z.infer<typeof productStatusSchema>;

/** Integer minor units (e.g. pence). Floats/decimals are rejected. */
export const minorAmountSchema = z
  .number()
  .int("amount must be integer minor units (no decimals)")
  .nonnegative();

/** ISO-4217 alpha-3 currency code, normalised to upper case. */
export const currencyCodeSchema = z
  .string()
  .length(3, "currency must be a 3-letter ISO code")
  .toUpperCase();

/** URL-safe slug: lower-case words joined by single hyphens. */
export const slugSchema = z
  .string()
  .min(1)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "invalid slug");

/** Optional 6-digit hex colour swatch (with or without leading '#'). */
export const swatchHexSchema = z
  .string()
  .regex(/^#?[0-9a-fA-F]{6}$/, "swatch must be a 6-digit hex colour")
  .nullish();

export const productOptionValueInputSchema = z.object({
  value: z.string().min(1),
  position: z.number().int().nonnegative().default(0),
  swatchHex: swatchHexSchema,
});
export type ProductOptionValueInput = z.infer<
  typeof productOptionValueInputSchema
>;

export const productOptionInputSchema = z.object({
  name: z.string().min(1),
  position: z.number().int().nonnegative().default(0),
  values: z.array(productOptionValueInputSchema).min(1),
});
export type ProductOptionInput = z.infer<typeof productOptionInputSchema>;

/** A minor-unit amount paired with its currency (P1-02). */
export const moneySchema = z.object({
  amountMinor: minorAmountSchema,
  currency: currencyCodeSchema,
});
export type Money = z.infer<typeof moneySchema>;

/** Tax-class write shape (P1-02). `rateBps` is basis points (1% = 100 bps). */
export const taxClassInputSchema = z.object({
  name: z.string().min(1),
  code: z
    .string()
    .min(1)
    .regex(/^[A-Za-z0-9_-]+$/, "code must be alphanumeric"),
  rateBps: z.number().int().nonnegative().max(100_000).nullish(),
  isDefault: z.boolean().default(false),
});
export type TaxClassInput = z.infer<typeof taxClassInputSchema>;

export const productVariantInputSchema = z
  .object({
    sku: z.string().min(1).nullish(),
    barcode: z.string().min(1).nullish(),
    priceMinor: minorAmountSchema,
    compareAtMinor: minorAmountSchema.nullish(),
    costMinor: minorAmountSchema.nullish(),
    currency: currencyCodeSchema,
    stock: z.number().int().nonnegative().default(0),
    isPreorder: z.boolean().default(false),
    backorderable: z.boolean().default(false),
    weightGrams: z.number().int().nonnegative().nullish(),
    imageId: z.string().nullish(),
    isDefault: z.boolean().default(false),
    isEnabled: z.boolean().default(true),
    position: z.number().int().nonnegative().default(0),
    // Option-value identifiers (or values, resolved by the caller) this variant
    // maps to — one per option. Encodes the row's cell in the variant matrix.
    optionValues: z.array(z.string()).default([]),
  })
  // A compare-at (was-) price only makes sense strictly above the price (P1-02).
  .refine((v) => v.compareAtMinor == null || v.compareAtMinor > v.priceMinor, {
    message: "compareAtMinor must be greater than priceMinor",
    path: ["compareAtMinor"],
  });
export type ProductVariantInput = z.infer<typeof productVariantInputSchema>;

// ─── P1-07 · Admin list/editor shapes ───────────────────────────────────────

export const productListInputSchema = z
  .object({
    query: z.string().trim().optional(),
    // Omitted = everything except ARCHIVED (archiving hides from default lists).
    status: productStatusSchema.optional(),
    categoryId: z.string().optional(),
    manufacturerId: z.string().optional(),
    tagId: z.string().optional(),
    minPriceMinor: minorAmountSchema.optional(),
    maxPriceMinor: minorAmountSchema.optional(),
    readiness: z.enum(CATALOG_READINESS_VALUES).optional(),
    availability: z.enum(["IN_STOCK", "LOW", "BACKORDER", "PREORDER", "OUT_OF_STOCK", "LOW_STOCK"]).optional(),
    sort: z.enum(["updatedAt", "title", "createdAt", "price"]).default("updatedAt"),
    dir: z.enum(["asc", "desc"]).default("desc"),
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).default(20),
  })
  .refine(
    (filters) =>
      filters.minPriceMinor === undefined ||
      filters.maxPriceMinor === undefined ||
      filters.minPriceMinor <= filters.maxPriceMinor,
    { message: "minimum price must not exceed maximum price" },
  )
  .refine(
    (filters) =>
      filters.readiness !== "ready-to-publish" ||
      filters.status === undefined ||
      filters.status === "DRAFT",
    { message: "ready-to-publish is limited to drafts" },
  );

/** Minimal create — the editor opens on General and fills in the rest. */
export const productCreateSchema = z.object({
  title: z.string().min(1),
  slug: slugSchema.optional(),
  description: z.string().max(100_000).optional(),
  priceMinor: minorAmountSchema.optional(),
  sku: z.string().trim().min(1).max(120).optional(),
  categoryId: z.string().optional(),
  initialStock: z.number().int().nonnegative().optional(),
  clientRequestId: z.string().uuid().optional(),
  mediaAssetIds: z.array(z.string()).max(20).optional(),
  templateId: z.string().optional(),
});

export const productTemplateSnapshotSchema = z.object({
  description: z.string().max(100_000).nullish(),
  categoryIds: z.array(z.string()).max(20).default([]),
  mediaAssetIds: z.array(z.string()).max(20).default([]),
  manufacturerId: z.string().nullish(),
  taxClassId: z.string().nullish(),
  shippingClassId: z.string().nullish(),
  options: z
    .array(
      z.object({
        name: z.string().trim().min(1),
        values: z.array(z.string().trim().min(1)).min(1),
      }),
    )
    .max(3)
    .default([]),
});
export const productTemplateSaveSchema = z.object({
  id: z.string().optional(),
  name: z.string().trim().min(1).max(100),
  description: z.string().max(500).nullish(),
  snapshot: productTemplateSnapshotSchema,
});
export const productTemplateDeleteSchema = z.object({ id: z.string() });

/**
 * Editor patch — absent key = leave unchanged, explicit null = clear (same
 * convention as the manufacturer/category update schemas). Status moves via
 * `setStatus`, never here. Variant-matrix editing is P1-08; only the default
 * variant's pricing is patchable from the Pricing tab.
 */
export const productPatchSchema = z.object({
  id: z.string(),
  // Optional during the compatible rollout: callers that send it get an
  // explicit stale-editor conflict; legacy callers still use the server's
  // freshly-read token in the atomic compare-and-write below.
  revision: z.number().int().positive().optional(),
  title: z.string().min(1).optional(),
  slug: slugSchema.optional(),
  description: z.string().nullish(),
  descriptionJson: z.unknown().nullish(),
  videoUrl: z.string().url().nullish(),
  manufacturerId: z.string().nullish(),
  taxClassId: z.string().nullish(),
  fitDetailsVisible: z.boolean().nullable().optional(),
  metaTitle: z.string().nullish(),
  metaDescription: z.string().nullish(),
  // Replace-set when present.
  categoryIds: z.array(z.string()).optional(),
  // Create-on-type tag names; replace-set when present.
  tagNames: z.array(z.string().min(1)).optional(),
  defaultVariant: z
    .object({
      priceMinor: minorAmountSchema.optional(),
      compareAtMinor: minorAmountSchema.nullish(),
      costMinor: minorAmountSchema.nullish(),
    })
    .optional(),
});
export type ProductPatch = z.infer<typeof productPatchSchema>;

export const productSetStatusSchema = z.object({
  id: z.string(),
  revision: z.number().int().positive().optional(),
  status: productStatusSchema,
});

// ─── P1-08 · Variant matrix editor shapes ────────────────────────────────────

/**
 * Option-builder replace-set: the full desired option/value tree for a product.
 * `id` present = keep/rename that row (values survive, so renaming "Color" →
 * "Colour" never orphans variants); absent = create. Rows missing from the
 * input are deleted (value deletion cascades its variant joins — the following
 * `variants.generate` then surfaces those variants as orphans to confirm).
 */
export const optionValueSetSchema = z.object({
  id: z.string().optional(),
  value: z.string().trim().min(1),
  swatchHex: swatchHexSchema,
});

export const optionsSetSchema = z.object({
  productId: z.string(),
  revision: z.number().int().positive().optional(),
  options: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().trim().min(1),
        values: z.array(optionValueSetSchema).min(1),
      }),
    )
    // MAX_OPTIONS (variant-matrix.ts) — literal here so Zod owns the message.
    .max(3, "A product supports at most 3 options."),
});
export type OptionsSetInput = z.infer<typeof optionsSetSchema>;

export const variantsGenerateSchema = z.object({
  productId: z.string(),
  revision: z.number().int().positive().optional(),
});

/**
 * One inline-edited grid row — patch-style (absent = unchanged, null = clear).
 * The compareAt > price rule needs the row's *effective* values, so it's
 * enforced in the procedure after merging onto the stored row.
 */
export const variantRowPatchSchema = z.object({
  id: z.string(),
  revision: z.number().int().positive().optional(),
  sku: z.string().trim().min(1).nullish(),
  barcode: z.string().trim().min(1).nullish(),
  priceMinor: minorAmountSchema.optional(),
  compareAtMinor: minorAmountSchema.nullish(),
  costMinor: minorAmountSchema.nullish(),
  stock: z.number().int().nonnegative().optional(),
  isPreorder: z.boolean().optional(),
  backorderable: z.boolean().optional(),
  weightGrams: z.number().int().nonnegative().nullish(),
  imageId: z.string().nullish(),
  isEnabled: z.boolean().optional(),
});
export type VariantRowPatch = z.infer<typeof variantRowPatchSchema>;

export const variantsBatchUpsertSchema = z.object({
  productId: z.string(),
  revision: z.number().int().positive().optional(),
  rows: z.array(variantRowPatchSchema).min(1).max(2000),
});

export const variantsDeleteSchema = z.object({
  productId: z.string(),
  revision: z.number().int().positive().optional(),
  ids: z.array(z.string()).min(1),
});

export const variantSetDefaultSchema = z.object({
  productId: z.string(),
  revision: z.number().int().positive().optional(),
  variantId: z.string(),
});

export const productInputSchema = z.object({
  title: z.string().min(1),
  slug: slugSchema,
  description: z.string().nullish(),
  descriptionJson: z.unknown().nullish(),
  videoUrl: videoUrlSchema.nullish(),
  status: productStatusSchema.default("DRAFT"),
  manufacturerId: z.string().nullish(),
  taxClassId: z.string().nullish(),
  // Products link to many categories via ProductCategory (P1-04).
  categoryIds: z.array(z.string()).default([]),
  options: z.array(productOptionInputSchema).default([]),
  // Always at least one variant — even "simple" products carry a single default
  // variant so cart/checkout has one uniform path (P1-01 open question).
  variants: z.array(productVariantInputSchema).min(1),
  tags: z.array(z.string().min(1)).default([]),
});
export type ProductInput = z.infer<typeof productInputSchema>;
